import React, { useEffect } from 'react';

type EnvShape = {
  MODE?: string;
  PROD?: boolean;
  DEV?: boolean;
  VITE_SENTRY_TEST_KEY?: string;
};

function readEnv(): EnvShape {
  try {
    return (import.meta as unknown as { env?: EnvShape }).env ?? {};
  } catch {
    return {};
  }
}

function getQueryKey(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('key');
  } catch {
    return null;
  }
}

function ThrowOnMount(): React.ReactElement {
  // Throw during render — both Sentry's ErrorBoundary and the legacy
  // ErrorBoundary will catch it, so the page never goes blank.
  throw new Error('Sentry verification ping from civix-web');
}

function NotFound(): React.ReactElement {
  useEffect(() => {
    try {
      document.title = '404 — Not Found';
    } catch {
      /* noop */
    }
  }, []);
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#cfcfe0',
        background: '#0A0A0F',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
      data-testid="sentry-verify-404"
    >
      <h1 style={{ fontSize: 28, fontWeight: 600 }}>404 — Not Found</h1>
    </div>
  );
}

export function isSentryVerifyAllowed(env: EnvShape, queryKey: string | null): boolean {
  // In dev (or any non-production mode) the route is always available.
  if (!env.PROD) return true;
  const expected = env.VITE_SENTRY_TEST_KEY;
  if (!expected) return false;
  return queryKey != null && queryKey === expected;
}

export default function SentryVerifyRoute(): React.ReactElement {
  const env = readEnv();
  const queryKey = getQueryKey();
  if (!isSentryVerifyAllowed(env, queryKey)) {
    return <NotFound />;
  }
  return <ThrowOnMount />;
}
